/**
 * iter642 — Geidea Payment Terminal settings.
 * Tenant admin enters merchant_id / terminal_id / api_key here.
 * Until a merchant supplies real credentials, the system runs in
 * SIMULATOR mode (random approve/decline ~85%). The user can flip
 * to live mode once Geidea provisions the merchant.
 */
import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { CreditCard, KeyRound, Cpu, AlertCircle } from 'lucide-react';
import api from '@/lib/api';

export default function GeideaSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    geidea_mode: 'simulator',
    geidea_terminal_id: '',
    geidea_merchant_id: '',
    geidea_api_key: '',
    geidea_base_url: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/payments/geidea/settings/current')
      .then(r => { setSettings(r.data); setForm(f => ({ ...f, ...r.data, geidea_api_key: '' })); })
      .catch(() => setSettings({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.geidea_api_key) delete payload.geidea_api_key;
      const { data } = await api.put('/payments/geidea/settings', payload);
      setSettings(data);
      setForm(f => ({ ...f, ...data, geidea_api_key: '' }));
      toast.success('Geidea settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const isLive = form.geidea_mode === 'live';

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5" data-testid="geidea-settings-page">
        <header className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md">
            <CreditCard size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit">Geidea Terminal</h1>
            <p className="text-sm text-muted-foreground">Card-machine integration for in-restaurant card payments.</p>
          </div>
        </header>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-outfit flex items-center gap-2">
              <Cpu size={15} /> Mode
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg border bg-stone-50 dark:bg-stone-900">
              <div>
                <Label className="text-sm font-semibold">Live Geidea Mode</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {isLive ? 'Real charges will be made on the connected terminal.' : 'Simulator mode — every sale is faked with an 85% approval rate.'}
                </p>
              </div>
              <Switch
                checked={isLive}
                onCheckedChange={(v) => setForm(f => ({ ...f, geidea_mode: v ? 'live' : 'simulator' }))}
                data-testid="geidea-mode-toggle"
              />
            </div>
            {isLive && !settings?.has_api_key && (
              <div className="flex items-start gap-2 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-3 text-xs">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>
                  No API key on file. Live mode will fail until you paste your Geidea
                  <strong> API key</strong> below. You can find it in the Geidea Merchant Portal under Developer → API Keys.
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-outfit flex items-center gap-2">
              <KeyRound size={15} /> Credentials
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Merchant ID</Label>
              <Input
                value={form.geidea_merchant_id || ''}
                onChange={(e) => setForm(f => ({ ...f, geidea_merchant_id: e.target.value }))}
                placeholder="MERCHANT_XXXXXX"
                data-testid="geidea-merchant-id"
              />
            </div>
            <div>
              <Label className="text-xs">Terminal ID</Label>
              <Input
                value={form.geidea_terminal_id || ''}
                onChange={(e) => setForm(f => ({ ...f, geidea_terminal_id: e.target.value }))}
                placeholder="TERM_XXXXXX"
                data-testid="geidea-terminal-id"
              />
            </div>
            <div>
              <Label className="text-xs">API Key (write-only)</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={form.geidea_api_key || ''}
                onChange={(e) => setForm(f => ({ ...f, geidea_api_key: e.target.value }))}
                placeholder={settings?.has_api_key ? '•••••••••••••• (saved — paste new to replace)' : 'Paste your Geidea secret API key'}
                data-testid="geidea-api-key"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Stored encrypted server-side. Never echoed back to the client.
              </p>
            </div>
            <div>
              <Label className="text-xs">Base URL (override)</Label>
              <Input
                value={form.geidea_base_url || ''}
                onChange={(e) => setForm(f => ({ ...f, geidea_base_url: e.target.value }))}
                placeholder="https://api.geidea.net (leave empty for default)"
                data-testid="geidea-base-url"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="geidea-save-btn" className="min-w-[140px]">
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
